import React, { useEffect, useState } from 'react';
import { useCurrentLocation } from 'src/components/Routes/LocationRoute';
import { Formik, Form } from 'formik';
import {
  Button,
  CircularProgress,
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  FormControl,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  Switch,
  Box
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useUpdateLocation } from 'src/queries/locations';
import Expandable from '../../components/Expandable';
import AdHocDaysCalendar from '../../components/AdHocDaysCalendar';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import EnhancedCreateMerchant from '../../components/CreateLocationForm';
import { Merchant } from 'src/interfaces/interfaces';
import { uploadToS3 } from 'src/utils/s3Upload';

// Define the operating hours type
interface OperatingHour {
  dayOfWeek: number;
  isOpen: boolean;
  openTime: string;
  closeTime: string;
}

// Define the form values type
interface OperatingHoursFormValues {
  operating_hours: OperatingHour[];
}

// Define default operating hours structure
const getDefaultOperatingHours = (): OperatingHour[] => [
  { dayOfWeek: 0, isOpen: false, openTime: '08:00', closeTime: '17:00' },
  { dayOfWeek: 1, isOpen: false, openTime: '08:00', closeTime: '17:00' },
  { dayOfWeek: 2, isOpen: false, openTime: '08:00', closeTime: '17:00' },
  { dayOfWeek: 3, isOpen: false, openTime: '08:00', closeTime: '17:00' },
  { dayOfWeek: 4, isOpen: false, openTime: '08:00', closeTime: '17:00' },
  { dayOfWeek: 5, isOpen: false, openTime: '08:00', closeTime: '17:00' },
  { dayOfWeek: 6, isOpen: false, openTime: '08:00', closeTime: '17:00' }
];

const MerchantPage: React.FC = () => {
  const { merchant } = useCurrentLocation();
  const updateLocation = useUpdateLocation();

  const [editingMerchant, setEditingMerchant] = useState<Merchant>(merchant);
  const [editedField, setEditedField] = useState('');
  // The new logo uploads to S3 the moment it's picked, before Save; track its
  // progress so the button can wait and the user sees what's happening.
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const [operatingHoursChanged, setOperatingHoursChanged] = useState(false);
  // Store form initial values in state to handle dynamic updates
  const [operatingHoursInitialValues, setOperatingHoursInitialValues] = useState<OperatingHoursFormValues>({
    operating_hours: getDefaultOperatingHours()
  });
  // Track whether operating hours have been initialized from the merchant data
  const [operatingHoursInitialized, setOperatingHoursInitialized] = useState(false);

  // No fetch on mount. The record arrives already loaded through the route, and
  // this effect was a third request for data the route had just fetched — one of
  // the reasons every tab visit re-requested the same location.

  const parseTimeString = (timeString: string): string => {
    // Extract hours and minutes directly from the ISO string
    const matches = timeString.match(/T(\d{2}):(\d{2})/);
    if (!matches) return "00:00";

    const [_, hours, minutes] = matches;
    return `${hours}:${minutes}`;
  };

  // Update local state when merchant data changes
  useEffect(() => {
    setEditingMerchant(merchant);
    
    if (merchant.operatingHours) {
      // Create a new operating hours array with default values
      const updatedOperatingHours = getDefaultOperatingHours();
      
      // Update with merchant operating hours
      for (const operatingHour of merchant.operatingHours) {
        const dayIndex = operatingHour.dayOfWeek;
        updatedOperatingHours[dayIndex].isOpen = true;
        updatedOperatingHours[dayIndex].openTime = parseTimeString(operatingHour.openTime);
        updatedOperatingHours[dayIndex].closeTime = parseTimeString(operatingHour.closeTime);
      }
      
      // Update the initial values state with new object reference to trigger Formik update
      setOperatingHoursInitialValues({
        operating_hours: [...updatedOperatingHours]
      });
      
      // Set the flag that operating hours have been initialized
      setOperatingHoursInitialized(true);
    }
  }, [merchant.operatingHours]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditingMerchant((prev) => ({ ...prev, [name]: value }));
    setEditedField(name);
  };

  const handleAddressChange = (
    place: google.maps.places.PlaceResult,
    placeId: string | undefined,
    lat: number | undefined,
    lng: number | undefined
  ) => {
    if (place.address_components) {
      const addressComponents = place.address_components;
      const streetNumber = getAddressComponent(addressComponents, 'street_number');
      const route = getAddressComponent(addressComponents, 'route');
      const city = getAddressComponent(addressComponents, 'locality') || getAddressComponent(addressComponents, 'postal_town');

      const formattedAddress = {
        street: streetNumber && route ? `${streetNumber} ${route}` : route,
        city: city,
        province: getAddressComponent(addressComponents, 'administrative_area_level_1'),
        zip: getAddressComponent(addressComponents, 'postal_code'),
        country: getAddressComponent(addressComponents, 'country'),
        countryCode: getAddressComponent(addressComponents, 'country', true),
        placeId: placeId,
        lat: lat,
        lng: lng
      };
      setEditingMerchant((prev) => ({
        ...prev,
        address: formattedAddress
      }));
      setEditedField('address');
    }
  };

  const getAddressComponent = (
    components: google.maps.GeocoderAddressComponent[],
    type: string,
    shortName: boolean = false
  ): string => {
    const component = components.find(comp => comp.types.includes(type));
    return shortName ? component ? component.short_name : '' : component ? component.long_name : '';
  };

  const handleSave = () => {
    if (editedField === 'streakTarget' || editedField === 'totalTarget') {
      updateLocation.mutate({
        id: merchant.id,
        [editedField]: parseInt(
          editingMerchant[editedField as keyof Merchant] as string
        )
      });
    } else {
      updateLocation.mutate({
        id: merchant.id,
        [editedField]: editingMerchant[editedField as keyof Merchant]
      });
    }
  };

  const handleBillingInfoSave = () => {
    const billingInfo = {
      billingEmail: editingMerchant.billingEmail,
      billingBusinessName: editingMerchant.billingBusinessName,
      billingContactName: editingMerchant.billingContactName,
      billingBusinessRegistrationNumber: editingMerchant.billingBusinessRegistrationNumber,
      billingTaxNumber: editingMerchant.billingTaxNumber
    };

    updateLocation.mutate({ id: merchant.id, ...billingInfo });
  };

  const handleOperatingHoursSubmit = async (values: any) => {
    values.operating_hours = values.operating_hours.filter(
      (hours: any) => hours.isOpen
    );
    try {
      await updateLocation.mutateAsync({
        id: merchant.id,
        operating_hours: values.operating_hours
      });

      setOperatingHoursChanged(false);
      // No explicit refetch: the mutation invalidates this location's key, so the
      // record reloads on its own. The old code did both, which is why saving
      // operating hours fired two requests.
      
    } catch (error) {
      console.error('Error updating merchant:', error);
    }
  };

  const handleAdHocClosingDaysSubmit = async (values: any) => {
    try {
      updateLocation.mutate({ id: merchant.id, ad_hoc_closing_days: values });
    } catch (error) {
      console.error('Error updating merchant:', error);
    }
  };

  const handleStreaksEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditingMerchant((prev) => ({ ...prev, streaksEnabled: event.target.checked }));
    setEditedField('streaksEnabled');
  };

  const nrToDayMapping = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  const timeOptions = generateTimeOptions();

  function generateTimeOptions() {
    const options = [];
    for (let hour = 5; hour <= 23; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute
          .toString()
          .padStart(2, '0')}`;
        options.push(time);
      }
    }
    return options;
  }

  const handleOperatingHoursChange = (
    index: number,
    field: string,
    value: any,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue(`operating_hours.${index}.${field}`, value);
    setOperatingHoursChanged(true);

    // If it's Monday (index 1) and the field is openTime or closeTime,
    // update all other days including Sunday (index 0) and Saturday (index 6)
    if (index === 1 && (field === 'openTime' || field === 'closeTime')) {
      for (let i = 0; i <= 6; i++) {
        if (i !== 1) { // Skip Monday as it's already set
          setFieldValue(`operating_hours.${i}.${field}`, value);
        }
      }
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) {
      return;
    }

    const file = event.target.files[0];
    setLogoUploadError(null);
    setIsLogoUploading(true);
    try {
      const uploadedUrl = await uploadToS3(file);
      setEditingMerchant(prev => ({ ...prev, logoUrl: uploadedUrl }));
      setEditedField('logoUrl');
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      setLogoUploadError(
        `Failed to upload logo: ${error?.message || 'Network error'}`
      );
    } finally {
      setIsLogoUploading(false);
    }
  };

  if (updateLocation.isPending) {
    return (
      <Container
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={{ width: '100%' }}>
      {merchant.id <= 0 || merchant.id === undefined ? (
        <div>
          <Typography variant='h4' gutterBottom>
            Please register your location
          </Typography>
          <EnhancedCreateMerchant onComplete={() => {}} isCoffeeShop={true} />
          <Button
            form='merchant-submit-form'
            variant='contained'
            color='primary'
            type='submit'
          >
            Create Merchant
          </Button>
        </div>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper style={{ padding: '20px', textAlign: 'center' }}>
              <Expandable>
                <Expandable.Closed>
                  <img
                    src={merchant.logoUrl}
                    alt={merchant.name}
                    style={{ width: '300px', margin: '0 auto' }}
                  />
                </Expandable.Closed>
                <Expandable.Open>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                    <img
                      src={editingMerchant.logoUrl}
                      alt={editingMerchant.name}
                      style={{ width: '300px', margin: '0 auto' }}
                    />
                    <Button
                      variant="contained"
                      component="label"
                      disabled={isLogoUploading}
                      startIcon={
                        isLogoUploading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <CloudUploadIcon />
                        )
                      }
                    >
                      {isLogoUploading ? 'Uploading…' : 'Upload New Logo'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleLogoUpload}
                      />
                    </Button>
                    {logoUploadError && (
                      <Typography color="error" variant="caption">
                        {logoUploadError}
                      </Typography>
                    )}
                  </div>
                </Expandable.Open>
                <Expandable.OpenButton>
                  <EditIcon />
                </Expandable.OpenButton>
                <Expandable.CloseButton>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={handleSave}
                    disabled={isLogoUploading}
                  >
                    Save Changes
                  </Button>
                </Expandable.CloseButton>
              </Expandable>
              <Expandable>
                <Expandable.Closed>
                  <Typography variant='h4' gutterBottom>
                    {merchant.name}
                  </Typography>
                </Expandable.Closed>
                <Expandable.Open>
                  <TextField
                    variant='standard'
                    size='small'
                    label='Name'
                    name='name'
                    value={editingMerchant.name}
                    onChange={handleInputChange}
                  />
                </Expandable.Open>
                <Expandable.OpenButton>
                  <EditIcon />
                </Expandable.OpenButton>
                <Expandable.CloseButton>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={handleSave}
                  >
                    Save Changes
                  </Button>
                </Expandable.CloseButton>
              </Expandable>
              <Expandable>
                <Expandable.Closed>
                  <Typography variant='subtitle1' gutterBottom>
                    {merchant.location} ●{' '}
                    {merchant.type.display_name}
                  </Typography>
                </Expandable.Closed>
                <Expandable.Open>
                  <TextField
                    variant='standard'
                    size='small'
                    className='w-full mb-4'
                    label='Location'
                    name='location'
                    value={editingMerchant.location}
                    onChange={handleInputChange}
                    sx={{ mb: 2 }}
                  />
                </Expandable.Open>
                <Expandable.OpenButton>
                  <EditIcon />
                </Expandable.OpenButton>
                <Expandable.CloseButton>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={handleSave}
                  >
                    Save Changes
                  </Button>
                </Expandable.CloseButton>
              </Expandable>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper style={{ padding: '20px' }}>
              <Typography variant='h4' gutterBottom sx={{ textAlign: 'center' }}>
                Address
              </Typography>
              <Expandable>
                <Expandable.Closed>
                  <Typography>{`${merchant.address?.street}, ${merchant.address?.city}, ${merchant.address?.province}, ${merchant.address?.zip}, ${merchant.address?.country}`}</Typography>
                  {merchant.address?.placeId && (
                    <Typography>Place ID: {merchant.address.placeId}</Typography>
                  )}
                  {merchant.address?.lat && merchant.address?.lng && (
                    <Typography>Coordinates: {merchant.address.lat}, {merchant.address.lng}</Typography>
                  )}
                </Expandable.Closed>
                <Expandable.Open>
                  <>
                    <AddressAutocomplete onSelect={handleAddressChange} />
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Current Address:</Typography>
                    <Typography>{editingMerchant.address?.street}</Typography>
                    <Typography>{editingMerchant.address?.city}</Typography>
                    <Typography>{editingMerchant.address?.province}</Typography>
                    <Typography>{editingMerchant.address?.zip}</Typography>
                    <Typography>{editingMerchant.address?.country}</Typography>
                  </>
                </Expandable.Open>
                <Expandable.OpenButton>
                  <EditIcon />
                </Expandable.OpenButton>
                <Expandable.CloseButton>
                  <Button variant='contained' color='primary' onClick={handleSave}>
                    Save Changes
                  </Button>
                </Expandable.CloseButton>
              </Expandable>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper style={{ padding: '20px' }}>
              <Typography
                variant='h4'
                gutterBottom
                sx={{ textAlign: 'center', paddingBottom: '20px' }}
              >
                Operating Hours
              </Typography>

              {/* Only render Formik when operating hours have been initialized */}
              {operatingHoursInitialized ? (
                <Formik
                  initialValues={operatingHoursInitialValues}
                  onSubmit={handleOperatingHoursSubmit}
                  enableReinitialize={true}
                  key={JSON.stringify(operatingHoursInitialValues)}
                >
                  {({ errors, touched, setFieldValue, values }) => (
                    <Form id='edit-operating-hours-form'>
                      {values.operating_hours.map((hours: OperatingHour, index: number) => (
                        <Grid
                          container
                          spacing={2}
                          key={index}
                          alignItems='center'
                          style={{ paddingBottom: '15px' }}
                        >
                          <Grid item xs={4}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={hours.isOpen}
                                  onChange={(e) => {
                                    handleOperatingHoursChange(index, 'isOpen', e.target.checked, setFieldValue);
                                  }}
                                />
                              }
                              label={nrToDayMapping[hours.dayOfWeek]}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <FormControl fullWidth disabled={!hours.isOpen}>
                              <InputLabel>Open</InputLabel>
                              <Select
                                value={hours.openTime}
                                onChange={(e) => {
                                  handleOperatingHoursChange(index, 'openTime', e.target.value, setFieldValue);
                                }}
                              >
                                {timeOptions.map((time) => (
                                  <MenuItem key={time} value={time}>
                                    {time}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={4}>
                            <FormControl fullWidth disabled={!hours.isOpen}>
                              <InputLabel>Close</InputLabel>
                              <Select
                                value={hours.closeTime}
                                onChange={(e) => {
                                  handleOperatingHoursChange(index, 'closeTime', e.target.value, setFieldValue);
                                }}
                              >
                                {timeOptions.map((time) => (
                                  <MenuItem key={time} value={time}>
                                    {time}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      ))}
                      
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          mt: 2
                        }}
                      >
                        <Button
                          type='submit'
                          variant='contained'
                          color='primary'
                          disabled={!operatingHoursChanged}
                        >
                          Save Operating Hours
                        </Button>
                      </Box>
                    </Form>
                  )}
                </Formik>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper
              style={{
                padding: '20px'
              }}
            >
              <Typography
                variant='h4'
                gutterBottom
                sx={{ textAlign: 'center' }}
              >
                Ad Hoc Closing Days
              </Typography>
              <Typography
                variant='body1'
                gutterBottom
                sx={{ textAlign: 'center', paddingBottom: '20px' }}
              >
                Select any other days when your store will be closed, such as
                public holidays
              </Typography>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <AdHocDaysCalendar
                  initialSelectedDates={merchant.adHocClosingDays || []}
                  onSubmit={(selectedDates) =>
                    handleAdHocClosingDaysSubmit(selectedDates)
                  }
                />
              </div>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper style={{ padding: '20px' }}>
              <Typography
                variant='h4'
                gutterBottom
                sx={{ textAlign: 'center' }}
              >
                Targets
              </Typography>
              <Expandable>
                <Expandable.Closed>
                  <Typography sx={{ mb: 2, mr: 2 }}>
                    Total Target: {merchant.totalTarget}
                  </Typography>
                  <Typography sx={{ mb: 2, mr: 2 }}>
                    Streak Target: {merchant.streakTarget}
                  </Typography>
                </Expandable.Closed>
                <Expandable.Open>
                  <div>
                    <TextField
                      variant='standard'
                      size='small'
                      className='w-full mb-4'
                      label='Total Target'
                      name='totalTarget'
                      type="number"
                      inputProps={{ min: 0, max: 12 }}
                      value={editingMerchant.totalTarget}
                      onChange={handleInputChange}
                      sx={{ mb: 2, mr: 2, minWidth: '200px' }}
                    />
                    <TextField
                      variant='standard'
                      size='small'
                      className='w-full mb-4'
                      label='Streak Target'
                      name='streakTarget'
                      type="number"
                      inputProps={{ min: 0, max: 5 }}
                      value={editingMerchant.streakTarget}
                      onChange={handleInputChange}
                      sx={{ mb: 2, mr: 2, minWidth: '200px' }}
                    />
                  </div>
                </Expandable.Open>
                <Expandable.OpenButton>
                  <EditIcon />
                </Expandable.OpenButton>
                <Expandable.CloseButton>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={handleSave}
                  >
                    Save Changes
                  </Button>
                </Expandable.CloseButton>
              </Expandable>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper style={{ padding: '20px' }}>
              <Typography variant="h4" gutterBottom sx={{ textAlign: 'center' }}>
                Settings
              </Typography>
              <Expandable>
                <Expandable.Closed>
                  <Typography sx={{ mb: 2, mr: 2 }}>
                    Streaks Enabled: {merchant.streaksEnabled ? 'Yes' : 'No'}
                  </Typography>
                </Expandable.Closed>
                <Expandable.Open>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editingMerchant.streaksEnabled}
                        onChange={handleStreaksEnabledChange}
                        name="streaksEnabled"
                      />
                    }
                    label="Enable Streaks"
                  />
                </Expandable.Open>
                <Expandable.OpenButton>
                  <EditIcon />
                </Expandable.OpenButton>
                <Expandable.CloseButton>
                  <Button variant="contained" color="primary" onClick={handleSave}>
                    Save Changes
                  </Button>
                </Expandable.CloseButton>
              </Expandable>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper style={{ padding: '20px' }}>
              <Typography variant="h4" gutterBottom sx={{ textAlign: 'center' }}>
                Billing Information
              </Typography>
              <Expandable>
                <Expandable.Closed>
                  <Typography sx={{ mb: 2 }}>
                    Billing Email: {merchant.billingEmail}
                  </Typography>
                  <Typography sx={{ mb: 2 }}>
                    Billing Business Name: {merchant.billingBusinessName}
                  </Typography>
                  <Typography sx={{ mb: 2 }}>
                    Billing Contact Name: {merchant.billingContactName}
                  </Typography>
                  <Typography sx={{ mb: 2 }}>
                    Business Registration Number: {merchant.billingBusinessRegistrationNumber}
                  </Typography>
                  <Typography sx={{ mb: 2, mr: 2 }}>
                    Tax Number: {merchant.billingTaxNumber}
                  </Typography>
                </Expandable.Closed>
                <Expandable.Open>
                  <>
                    <TextField
                      variant='standard'
                      size='small'
                      fullWidth
                      label='Billing Email'
                      name='billingEmail'
                      value={editingMerchant.billingEmail}
                      onChange={handleInputChange}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      variant='standard'
                      size='small'
                      fullWidth
                      label='Billing Business Name'
                      name='billingBusinessName'
                      value={editingMerchant.billingBusinessName}
                      onChange={handleInputChange}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      variant='standard'
                      size='small'
                      fullWidth
                      label='Billing Contact Name'
                      name='billingContactName'
                      value={editingMerchant.billingContactName}
                      onChange={handleInputChange}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      variant='standard'
                      size='small'
                      fullWidth
                      label='Business Registration Number'
                      name='billingBusinessRegistrationNumber'
                      value={editingMerchant.billingBusinessRegistrationNumber}
                      onChange={handleInputChange}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      variant='standard'
                      size='small'
                      fullWidth
                      label='Tax Number'
                      name='billingTaxNumber'
                      value={editingMerchant.billingTaxNumber}
                      onChange={handleInputChange}
                      sx={{ mb: 2 }}
                    />
                  </>
                </Expandable.Open>
                <Expandable.OpenButton>
                  <EditIcon />
                </Expandable.OpenButton>
                <Expandable.CloseButton>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={handleBillingInfoSave}
                  >
                    Save Changes
                  </Button>
                </Expandable.CloseButton>
              </Expandable>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default MerchantPage;
